import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, Upload, Check } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';
import * as xlsx from 'xlsx';

const C = COLORS;

const TeacherBulkUpload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user?.role === 'manager' || user?.user_type === 'Teacher') {
      toast.error('Bulk upload is not permitted for your role.');
      navigate('/teachers');
    }
  }, [user, navigate]);

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const downloadTemplate = () => {
    const csvContent = "UDISE,First Name,Last Name,Title,Gender,Date of Birth,Marital Status,Spouse Name,PAN Number,Aadhaar Number,Experience Years,Experience Months,Phone Number,Email Address,Address,Subject,Status\n07080100101,Mary,Smith,Mrs,Female,1985-06-15,Married,John Smith,ABCDE1234F,123456789012,10,5,9876543210,mary@example.com,123 Main St,Math,Active";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Teacher_Bulk_Upload_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewData(null); // Reset preview when new file is selected
    }
  };

  const parseDate = (dateVal) => {
    if (!dateVal) return null;
    if (typeof dateVal === 'number') {
      const dObj = new Date(Math.round((dateVal - 25569) * 86400 * 1000));
      return isNaN(dObj) ? null : dObj.toISOString().split('T')[0];
    }
    const s = String(dateVal).trim();
    const match = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (match) {
      let [_, d, m, y] = match;
      if (y.length === 2) y = (parseInt(y) > 30 ? '19' : '20') + y;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const dObj = new Date(s.replace(/(st|nd|rd|th)/, ''));
    if (!isNaN(dObj)) return dObj.toISOString().split('T')[0];
    return null;
  };

  const handleLoad = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = xlsx.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName]);

          let parsedRows = [];
          for (const rawRow of rows) {
            const row = {};
            for (const key of Object.keys(rawRow)) {
              row[key.trim()] = rawRow[key];
            }

            const data = {
              udise: row['UDISE'] || row.udise || '',
              school_id: null,
              school_name: '',
              title: row['Title'] || row.title || row['Designation'] || '',
              first_name: row['First Name'] || row.first_name || row.Name || row['Employee Name [ID]'] || '',
              last_name: row['Last Name'] || row.last_name || '',
              gender: row['Gender'] || row.gender || '',
              dob: parseDate(row['Date of Birth'] || row.dob),
              marital_status: row['Marital Status'] || row.marital_status || '',
              spouse_name: row['Spouse Name'] || row.spouse_name || '',
              pan: row['PAN Number'] || row.pan || '',
              aadhaar: row['Aadhaar Number'] || row.aadhaar || '',
              experience_years: row['Experience Years'] || row.experience_years || 0,
              experience_months: row['Experience Months'] || row.experience_months || 0,
              phone: row['Phone Number'] || row.phone || row.Mobile || '',
              email: row['Email Address'] || row.email || row.Email || '',
              address: row['Address'] || row.address || '',
              subject_id: row['Subject_id'] || row.subject_id || null,
              status: row['Status'] || row.status || 'Active'
            };
            parsedRows.push(data);
          }
          setPreviewData(parsedRows);
          toast.success('File parsed successfully');
        } catch (parseErr) {
          console.error(parseErr);
          toast.error('Failed to parse file content');
        } finally {
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setIsUploading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error('Failed to load file');
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!previewData || previewData.length === 0) return;

    try {
      setIsUploading(true);
      const res = await api.post('/teachers/bulk-upload-confirm', { teachers: previewData });
      toast.success(res.data.message || 'Teachers uploaded successfully');
      setPreviewData(null);
      navigate('/teachers');
    } catch (err) {
      toast.error('Failed to insert teachers');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <AppLayout title="Teacher Bulk Upload">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/teachers')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
        >
          <ArrowLeft size={18} /> Back
        </button>
        <button type="button" onClick={downloadTemplate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
          <Download size={18} /> Template
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 32 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 32 }}>
          <div style={{ flex: 1, padding: '12px 16px', border: `1px dashed ${C.g300}`, borderRadius: 8, background: C.g50, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: file ? C.g900 : C.g500, fontWeight: 500 }}>
              {file ? file.name : 'No file selected'}
            </span>
            <input 
              type="file" 
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              style={{ display: 'none' }} 
            />
            <button 
              type="button" 
              onClick={() => fileInputRef.current.click()} 
              style={{ padding: '6px 12px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              Choose File
            </button>
          </div>
          
          <button 
            type="button" 
            onClick={handleLoad} 
            disabled={!file || isUploading}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (!file || isUploading) ? 'not-allowed' : 'pointer', opacity: (!file || isUploading) ? 0.7 : 1 }}
          >
            <Upload size={18} /> {isUploading && !previewData ? 'Loading...' : 'Load'}
          </button>
        </div>

        {previewData && (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>
              Preview Data ({previewData.length} records)
            </h3>
            
            <div style={{ overflowX: 'auto', border: `1px solid ${C.g200}`, borderRadius: 8, marginBottom: 32 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                    <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase' }}>Name</th>
                    <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase' }}>Gender</th>
                    <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase' }}>Phone</th>
                    <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase' }}>DOB</th>
                    <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase' }}>School UDISE</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.length > 0 ? (
                    previewData.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${C.g100}` }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.g900, fontWeight: 500 }}>{row.title ? `${row.title} ` : ''}{row.first_name} {row.last_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.g600 }}>{row.gender || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.g600 }}>{row.phone || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.g600 }}>{row.dob || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: C.g600 }}>{row.udise || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: C.g500 }}>
                        No valid records found in the file.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 24, borderTop: `1px solid ${C.g100}` }}>
              <button 
                type="button"
                onClick={handleSubmit}
                disabled={isUploading || previewData.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 32px', background: '#10B981', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: (isUploading || previewData.length === 0) ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)', opacity: (isUploading || previewData.length === 0) ? 0.7 : 1 }}
              >
                <Check size={18} /> {isUploading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default TeacherBulkUpload;
